'use client'

/**
 * <WettbewerbGallery /> — R102.6 (2026-05-31)
 *
 * Wrapper um <AwardGallery>, der die Bild-URLs zu festen Slots dynamisch aus
 * der Plattform-DB (/api/public/wettbewerb-images) holt. Die Slot->URL-Map wird
 * 1× pro Page-Load gefetcht und global gecached, damit mehrere Galerien auf
 * derselben Seite keine Mehrfach-Abrufe machen.
 *
 * Bild fehlt im Slot? → AwardGallery rendert nichts (statt Broken-Image).
 */

import { useEffect, useState } from 'react'
import { AwardGallery, type AwardGalleryItem } from '@/components/award/award-gallery'

interface WettbewerbGalleryProps {
  slots: Array<{ slot: string; alt: string; caption: string }>
  minColWidth?: number
  aspect?: string
}

// Global pro Page-Load (gecached, da 'use client' nur im Browser läuft).
let cachedMap: Record<string, string> | null = null
let inFlight: Promise<Record<string, string>> | null = null

async function fetchMap(): Promise<Record<string, string>> {
  if (cachedMap) return cachedMap
  if (inFlight) return inFlight
  inFlight = fetch('/api/public/wettbewerb-images', { credentials: 'same-origin' })
    .then((r) => (r.ok ? r.json() : { success: false }))
    .then((j) => {
      const m = (j?.success ? j.data : {}) as Record<string, string>
      cachedMap = m
      return m
    })
    .catch(() => {
      cachedMap = {}
      return {}
    })
    .finally(() => { inFlight = null })
  return inFlight
}

export function WettbewerbGallery({ slots, minColWidth, aspect }: WettbewerbGalleryProps) {
  const [map, setMap] = useState<Record<string, string> | null>(cachedMap)

  useEffect(() => {
    if (cachedMap) {
      setMap(cachedMap)
      return
    }
    let aborted = false
    fetchMap().then((m) => {
      if (!aborted) setMap(m)
    })
    return () => {
      aborted = true
    }
  }, [])

  if (!map) {
    // Initial-Render bevor Map gefetcht ist → reservierter Block, kein Layout-Jump
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fit, minmax(${minColWidth ?? 280}px, 1fr))`,
          gap: '0.85em',
        }}
      >
        {slots.map((s) => (
          <div
            key={s.slot}
            style={{
              aspectRatio: aspect ?? '16/9',
              background: 'color-mix(in oklch, var(--award-ink-2) 80%, transparent)',
              border: '1px dashed var(--award-line)',
              borderRadius: '14px',
              opacity: 0.5,
            }}
          />
        ))}
      </div>
    )
  }

  const items: AwardGalleryItem[] = slots
    .map((s) => {
      const url = map[s.slot]
      if (!url) return null
      return { src: url, alt: s.alt, caption: s.caption }
    })
    .filter(Boolean) as AwardGalleryItem[]

  if (items.length === 0) {
    return (
      <p
        style={{
          padding: '0.85em 1em',
          borderLeft: '3px solid var(--award-line)',
          background: 'color-mix(in oklch, var(--award-ink-2) 88%, transparent)',
          fontSize: '0.88em',
          color: 'var(--award-cirrus-3)',
          fontStyle: 'italic',
        }}
      >
        Die zugehörigen Bilder werden derzeit von der Administration hochgeladen
        und erscheinen hier in Kürze.
      </p>
    )
  }

  return <AwardGallery items={items} minColWidth={minColWidth} aspect={aspect} />
}
